import { useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../services/translation.service';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../Shared/TextFieldFormGroup';
import { SanitizerService } from '../../services/sanitizer.service';
import type IValidatable from '../Shared/validatable.interface';
import ValidationSummary from '../Shared/ValidationSummary';
import ValidationInfo from '../../validation/validation-info';
import Consent, { type IConsentState } from '../Shared/Consent';
import HoneyPot, { type IHoneyPot } from '../Shared/HoneyPot';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import DatePicker from '../Shared/DatePicker';
import { LanguageService } from '../../services/language.service';
import type TicketInspectionSurchargeFormApplication from './models/ticket-inspection-surcharge-appeal-application.model';
import SubmitFailed from '../Shared/SubmitFailed';
import SubmitSuccessful from '../Shared/SubmitSuccessful';
import { AnalyticsService } from '../../services/analytics-service';
import type { IServerValidationError } from '../Shared/validatable.interface';
import FileUpload, { type IFileUploadState } from '../Shared/FileUpload';
import { DateHelper } from '../../helpers/date.helper';
import { Status } from '../../hooks/fetch.hook';
import { DomHelper } from '../../helpers/dom.helper';
import LogService from '../../services/log-service/log.service';
import AntiforgeryService from '../../services/anti-forgery.service';

function TicketInspectionSurchargeForm() {
  // === STATE ===
  const [ocrNumberState, setOcrNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [dateState, setDateState] = useState({ value: DateTime.now().toFormat('yyyy-MM-dd'), validationInfo: new ValidationInfo() });
  const [descriptionState, setDescriptionState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [fileUploadState, setFileUploadState] = useState<IFileUploadState>({ value: [], validationInfo: new ValidationInfo() });
  const [firstNameState, setFirstNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [lastNameState, setLastNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [phoneNumberState, setPhoneNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });

  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);

  // === REFS ===
  const ocrNumberRef = useRef<IValidatable>(null);
  const dateRef = useRef<IValidatable>(null);
  const descriptionRef = useRef<IValidatable>(null);
  const fileUploadRef = useRef<IValidatable>(null);
  const firstNameRef = useRef<IValidatable>(null);
  const lastNameRef = useRef<IValidatable>(null);
  const emailRef = useRef<IValidatable>(null);
  const phoneNumberRef = useRef<IValidatable>(null);
  const consentRef = useRef<IValidatable>(null);
  const honeyPotRef = useRef<IHoneyPot>(null);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: ocrNumberRef.current!, validationInfo: ocrNumberState.validationInfo, setState: setOcrNumberState },
      { component: dateRef.current!, validationInfo: dateState.validationInfo, setState: setDateState },
      { component: descriptionRef.current!, validationInfo: descriptionState.validationInfo, setState: setDescriptionState },
      { component: fileUploadRef.current!, validationInfo: fileUploadState.validationInfo, setState: setFileUploadState },
      { component: firstNameRef.current!, validationInfo: firstNameState.validationInfo, setState: setFirstNameState },
      { component: lastNameRef.current!, validationInfo: lastNameState.validationInfo, setState: setLastNameState },
      { component: emailRef.current!, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: phoneNumberRef.current!, validationInfo: phoneNumberState.validationInfo, setState: setPhoneNumberState },
      { component: consentRef.current!, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.TicketInspectionSurchargeAppeal, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean = false;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!honeyPotRef.current!.isValid()) return;

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.TicketInspectionSurchargeAppeal, AnalyticsService.ErrorAction, e);
        });
        ValidationHelper.scrollToAndFocusOnValidationSummary();
        return;
      }

      setSubmitStatus(Status.loading);
      const surchargeApplication: TicketInspectionSurchargeFormApplication = {
        ocrNumber: ocrNumberState.value,
        date: dateState.value,
        description: descriptionState.value,
        firstName: firstNameState.value,
        lastName: lastNameState.value,
        email: emailState.value,
        phoneNumber: phoneNumberState.value,
        isConsentGiven: consentState.value,
      };

      const formData = new FormData();
      formData.append('ticketInspectionSurchargeFormInfo', JSON.stringify(surchargeApplication));
      fileUploadState.value.forEach((file, index) => {
        formData.append(`files[${index}]`, file.actualFile);
      });

      try {
        const response = await fetch(`/api/ticketinspectionsurchargeappeal?language=${LanguageService.getCurrentLanguage()}`, {
          method: 'POST',
          body: formData,
          headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.TicketInspectionSurchargeAppeal, 'Skickat', '');
        } else {
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            const responseBody = await response.json();
            if (!responseBody.errors) throw new Error('Missing errors');
            handleServerValidationErrors(responseBody.errors);
          }
          AnalyticsService.addEvent(AnalyticsService.TicketInspectionSurchargeAppeal, 'Skickat - misslyckat', response.status?.toString());
        }
      } catch (error) {
        LogService.logError(error, 'TicketInspectionSurchargeForm: Unhandled exception when submitting form');
        AnalyticsService.addEvent(AnalyticsService.TicketInspectionSurchargeAppeal, 'Skickat - fel');
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: undefined };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('ticket-inspection-surcharge-form')?.scrollIntoView();
      document.getElementById('ticket-inspection-surcharge-form-validation-summary')?.focus();
    } else {
      DomHelper.scrollAndFocus('ticket-inspection-surcharge-form-submit-result');
    }
  }

  // === COMMON TRANSLATIONS ===
  const translationFirstFieldHelpText = TranslationService.get('ticketinspectionsurchargepage.firstfieldhelptext');
  const translationThirdFieldHelpText = TranslationService.get('ticketinspectionsurchargepage.thirdfieldhelptext');

  return (
    <div id="ticket-inspection-surcharge-form" className={`p-md-3 ${submitStatus !== Status.success && submitStatus !== Status.failed ? 'border-md' : ''}`}>
      {TranslationService.get('ticketinspectionsurchargepage.hiddenheading') && <h2 className="visually-hidden">{TranslationService.get('ticketinspectionsurchargepage.hiddenheading')}</h2>}
      {submitStatus !== Status.success && (submitStatus !== Status.failed || submitErrorCode === 400) && (
        <>
          {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
          <ValidationSummary
            id="ticket-inspection-surcharge-form-validation-summary"
            validationSummaryErrors={validationSummaryErrors}
            className={validationSummaryErrors.length > 0 ? 'mb-3 mb-md-4' : ''}
          />
          <TextFieldFormGroup
            ref={ocrNumberRef}
            className={translationFirstFieldHelpText ? 'mb-1' : ''}
            labelText={TranslationService.get('ticketinspectionsurchargepage.ocrnumber')}
            required={true}
            ariaDescribedBy={translationFirstFieldHelpText ? 'ocr-number-help-text' : undefined}
            inputId="ocr"
            serverValidationName="ocrNumber"
            autocomplete="off"
            state={ocrNumberState}
            onChange={(value) => {
              setOcrNumberState({ ...ocrNumberState, value });
            }}
            onKeyUp={() => {
              setOcrNumberState({ ...ocrNumberState, validationInfo: new ValidationInfo() });
            }}
          />
          {translationFirstFieldHelpText && (
            <div
              id="ocr-number-help-text"
              className="mb-3 ticket-inspection-surcharge-appeal-form__field-help-text"
              dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(translationFirstFieldHelpText) }}
            ></div>
          )}
          <div className="w-100 mb-3">
            <DatePicker
              ref={dateRef}
              minDate={DateTime.now().minus({ days: 10 }).startOf('day').toFormat(DateHelper.DATE_FORMAT)}
              maxDate={DateTime.now().startOf('day').toFormat(DateHelper.DATE_FORMAT)}
              showAsteriskOnRequiredFields={true}
              labelText={TranslationService.get('ticketinspectionsurchargepage.daytext')}
              ariaDescribedBy={dateState.validationInfo.isInvalid ? 'date-error date-help-text' : 'date-help-text'}
              className="mb-1"
              state={dateState}
              onChange={(value) => {
                setDateState((ps) => ({ ...ps, value }));
              }}
            />
            <p id="date-help-text" className="mb-0">
              {TranslationService.get('ticketinspectionsurchargepage.dayhelptext')}
            </p>
          </div>
          <TextFieldFormGroup
            ref={descriptionRef}
            className={translationThirdFieldHelpText ? 'mb-1' : ''}
            labelText={TranslationService.get('ticketinspectionsurchargepage.describe')}
            required={true}
            ariaDescribedBy={translationThirdFieldHelpText ? 'description-help-text' : undefined}
            inputId="description"
            serverValidationName="description"
            autocomplete="none"
            renderAsTextArea={true}
            state={descriptionState}
            maxLength={2000}
            onChange={(value) => {
              setDescriptionState({ ...descriptionState, value });
            }}
            onKeyUp={() => {
              setDescriptionState({ ...descriptionState, validationInfo: new ValidationInfo() });
            }}
          />
          {translationThirdFieldHelpText && (
            <div
              id="description-help-text"
              className="ticket-inspection-surcharge-appeal-form__field-help-text"
              dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(translationThirdFieldHelpText) }}
            ></div>
          )}
          <hr className="my-4" />
          <FileUpload
            id="inspection-surcharge-file-upload"
            ref={fileUploadRef}
            infoText={TranslationService.get('ticketinspectionsurchargepage.uploadfilesinfo')}
            state={fileUploadState}
            onFilesAdded={(files) => {
              setFileUploadState((ps) => ({ ...ps, value: [...ps.value, ...files] }));
            }}
            onFileRemoved={(file) => {
              setFileUploadState({ ...fileUploadState, value: fileUploadState.value.filter((f) => f !== file), validationInfo: new ValidationInfo() });
            }}
          />
          <hr className="my-4" />
          <div className="row">
            <div className="col-12 col-md-6">
              <TextFieldFormGroup
                ref={firstNameRef}
                labelText={TranslationService.get('common.firstname')}
                required={true}
                inputId="first-name"
                serverValidationName="firstName"
                autocomplete="none"
                state={firstNameState}
                onChange={(value) => {
                  setFirstNameState({ ...firstNameState, value });
                }}
                onKeyUp={() => {
                  setFirstNameState({ ...firstNameState, validationInfo: new ValidationInfo() });
                }}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextFieldFormGroup
                ref={lastNameRef}
                labelText={TranslationService.get('common.lastname')}
                required={true}
                inputId="last-name"
                serverValidationName="lastName"
                autocomplete="none"
                state={lastNameState}
                onChange={(value) => {
                  setLastNameState({ ...lastNameState, value });
                }}
                onKeyUp={() => {
                  setLastNameState({ ...lastNameState, validationInfo: new ValidationInfo() });
                }}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextFieldFormGroup
                ref={emailRef}
                labelText={TranslationService.get('common.email')}
                required={true}
                inputId="email"
                serverValidationName="email"
                autocomplete="email"
                state={emailState}
                onChange={(value) => {
                  setEmailState({ ...emailState, value });
                }}
                onKeyUp={() => {
                  setEmailState({ ...emailState, validationInfo: new ValidationInfo() });
                }}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextFieldFormGroup
                ref={phoneNumberRef}
                labelText={TranslationService.get('common.phonenumber')}
                required={false}
                inputId="phone-number"
                serverValidationName="phoneNumber"
                autocomplete="tel"
                state={phoneNumberState}
                onChange={(value) => {
                  setPhoneNumberState({ ...phoneNumberState, value });
                }}
                onKeyUp={() => {
                  setPhoneNumberState({ ...phoneNumberState, validationInfo: new ValidationInfo() });
                }}
              />
            </div>
          </div>
          <Consent
            id="ticket-inspection-surcharge-consent"
            ref={consentRef}
            consentText={TranslationService.get('ticketinspectionsurchargepage.consenttext')}
            integrityPolicyInfo={TranslationService.get('ticketinspectionsurchargepage.integritypolicyinfo')}
            termsAndConditions={TranslationService.get('ticketinspectionsurchargepage.termsandconditions.heading')}
            state={consentState}
            onChange={(value) => {
              setConsentState({ value, validationInfo: new ValidationInfo() });
            }}
          />
          <HoneyPot ref={honeyPotRef} />
          <hr className="my-4" />
          <div className="d-grid">
            <button className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick} type="button">
              <span hidden={submitStatus === Status.loading}>{TranslationService.get('ticketinspectionsurchargepage.submit')}</span>
            </button>
          </div>
        </>
      )}
      <div id="ticket-inspection-surcharge-form-submit-result" role="alert" aria-live="polite">
        {submitStatus === Status.failed && submitErrorCode !== 400 && submitErrorCode !== 409 && (
          <>
            <SubmitFailed failedText={TranslationService.get('ticketinspectionsurchargepage.generalerrortext')} />
            <button
              className="btn btn-primary d-block mx-auto"
              onClick={() => {
                setSubmitStatus(Status.notperformed);
              }}
            >
              {TranslationService.get('ticketinspectionsurchargepage.dismissgeneralerror')}
            </button>
          </>
        )}
        {submitStatus === Status.failed && submitErrorCode === 409 && <SubmitFailed failedText={TranslationService.get('ticketinspectionsurchargepage.conflicterrortext')} />}
        {submitStatus === Status.success && <SubmitSuccessful successText={TranslationService.get('ticketinspectionsurchargepage.completedtext')} />}
      </div>
    </div>
  );
}

export default TicketInspectionSurchargeForm;
